import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useUpdate } from "@refinedev/core";
import Button from "components/button";
import { CollapsibleSection } from "components/collapsibleSection"
import { FullScreenLoading } from "components/fullScreenLoading";
import Input from "components/input"
import { Label } from "components/label"
import Select from "components/select";
import { IEditOrderBuyerForm, IOrderDetail } from "interfaces"
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { PHONE_CODE, REGEX_PHONE_NUMBER, REGEX_VALID_EMAIL, REGEX_PHONE_NUMBER_MIN_5 } from "utills/constants";
import { z } from "zod";

const EditBuyerInfoSchema = z.object({
  buyer_name: z.string().min(1, "This field is required"),
  buyer_email: z
    .string()
    .min(1, { message: "This field is required" })
    .refine((value) => 
      REGEX_VALID_EMAIL.test(value ?? ""), 
      'Invalid email address'
    ),
  buyer_phone: 
    z.string().min(1, "This field is required")
    .refine((value) => 
      REGEX_PHONE_NUMBER_MIN_5.test(value ?? ""), 
      'Invalid phone number'
    ),
  buyer_phone_area_code: z.string().min(1, "This field is required"),
});

export const EditBuyerSection = ({orderDetailData, refetch} : {orderDetailData: IOrderDetail | null, refetch: () => void;}) => {

  const { mutate: updateMutation, isLoading: isLoading } = useUpdate();

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IEditOrderBuyerForm, HttpError, IEditOrderBuyerForm>({
    defaultValues: {
      buyer_email: orderDetailData?.buyer.email ?? "",
      buyer_name: orderDetailData?.buyer.name ?? "",
      buyer_phone: orderDetailData?.buyer.phone ?? "",
      buyer_phone_area_code: orderDetailData?.buyer.phone_area_code ?? "",
    },
    resolver: zodResolver(EditBuyerInfoSchema),
  });
  
  const onSubmit = (values: IEditOrderBuyerForm) => {
    updateMutation(
      {
        resource: "checkout/orders",
        id: `${orderDetailData?.code}/buyer`,
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...values,
        },
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
          refetch();
        },
      }
    );
  };
  
  return (
    <>
      <CollapsibleSection 
        label="Buyer Information"
        defaultOpen={true}
      >
        <div className="grid gap-x-4 gap-y-6 px-4 py-4 text-sm sm:px-6 sm:grid-cols-6 lg:gap-x-6" style={{wordBreak: "break-word"}}>
          <div className="sm:col-span-3">
            <Label id="customer_code" name="Customer Code" />
            <Input
              id="customer_code"
              type="text"
              placeholder="Customer Code"
              value={orderDetailData?.buyer.customer_code}
              disabled={true}
            />
          </div>
          <div className="sm:col-span-3">
            <Label required id="buyer_name" name="Full name" />
            <Input
              id="buyer_name"
              placeholder="Full name"
              error={errors["buyer_name"]}
              {...register("buyer_name")}
            />
            {errors["buyer_name"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors?.buyer_name.message}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label required id="buyer_email" name="Email" />
            <Input
              id="buyer_email"
              placeholder="Email"
              error={errors["buyer_email"]}
              {...register("buyer_email")}
            />
            {errors["buyer_email"] ? (
              <p className="error-text">
                <span className="font-medium">
                  {errors?.buyer_email.message}
                </span>
              </p>
            ) : null}
          </div>
          <div className="sm:col-span-3">
            <Label 
              required
              id="buyer_phone" 
              name="Phone"
            />
            <div className="flex">
              <Controller
                control={control}
                name="buyer_phone_area_code"
                defaultValue="84"
                render={({ field: { onChange, value = "", ref } }) => {
                  const areaPhoneCodeArr = PHONE_CODE.map(item => ({label: item.label, value: item.code}));
                  return (
                    <Select 
                      ref={ref}
                      className="flex-1 w-[160px] !rounded-none inline-flex items-center !rounded-l-md"
                      options={areaPhoneCodeArr} 
                      id="buyer_phone_area_code"
                      value={areaPhoneCodeArr.find(c => c.value === value)}
                      onChange={(selected: {label: string; value: string;}) => onChange(selected?.value)}
                      error={errors["buyer_phone_area_code"]}
                    />
                  )
                }}
              />
              <Input
                id="buyer_phone"
                className={`${errors["buyer_phone"] ? "border-l-[1px] focus:!ml-0" : "border-l-0"} rounded-l-none focus:border-l-[1px] focus:ml-[-1px] z-[2]`}
                placeholder="Phone number"
                error={errors["buyer_phone"]}
                {...register("buyer_phone")}
              />
            </div>
            {errors["buyer_phone"] ? ( 
              <p className="error-text">
                <span className="font-medium">
                  {errors["buyer_phone"]?.message}
                </span>
              </p> )
            : null}
          </div>
          <div className="col-span-full overflow-hidden">
            <Button
              type="submit"
              className="w-full sm:w-32 h-11 float-right"
              onClick={handleSubmit(onSubmit)}
            >
              SAVE
            </Button>
          </div>
        </div>
      </CollapsibleSection>
      {isLoading ? <FullScreenLoading /> : <></>}
    </>
  )
}